import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { ms } from '@arc/shared/util-js';
import { authKeys, useSessionQuery } from './data';
const AuthStateContext = createContext({
  user: null,
  status: 'loading'
});
export function UserProvider({
  children
}) {
  const {
    data: user,
    isPending
  } = useSessionQuery();
  const authState = useMemo(() => user ? {
    status: 'logged-in',
    user
  } : {
    status: isPending ? 'loading' : 'logged-out',
    user: null
  }, [isPending, user]);
  useEffect(() => {
    if (user) {
      const userId = `${user.id}-${user.email}`;
      let updateClarityTimeout = undefined;
      const updateClarity = () => {
        if ('clarity' in window && typeof window.clarity === 'function') {
          window.clarity('identify', userId);
          window.clarity('set', 'user-name', user.name);
          window.clarity('set', 'user-email', user.email);
        } else {
          updateClarityTimeout = setTimeout(() => updateClarity(), 2 * ms.seconds);
        }
      };
      updateClarity();
      Sentry.setUser({
        id: userId
      });
      return () => {
        if (updateClarityTimeout !== undefined) {
          clearTimeout(updateClarityTimeout);
        }
      };
    } else {
      Sentry.setUser(null);
    }
  }, [user]);
  const queryClient = useQueryClient();
  useEffect(() => {
    // Reset queries when user changes.
    void queryClient.resetQueries({
      // Don't reset the session query because that will cause a loop.
      predicate: query => query.queryKey !== authKeys.session()
    });
  }, [queryClient, user?.id]);
  return <AuthStateContext.Provider value={authState}>
			{children}
		</AuthStateContext.Provider>;
}
export function useAuth() {
  const {
    user
  } = useContext(AuthStateContext);
  if (user === null) {
    throw new Error('UserContext is null. Do not call useAuth outside of authenticated region.');
  }
  return {
    user
  };
}

/** Same as {@link useAuth}, but will never throw errors. */
export function useAuthSafe() {
  return useContext(AuthStateContext);
}